import { useEffect, useMemo, useState } from "react";
import { DeliveryChip } from "../../components/DeliveryChip";
import { Link } from "wouter";
import { collection, doc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { FileText, Upload } from "lucide-react";
import {
  COMPANY_DOC_CATEGORIES,
  COMPANY_DOC_MAX_BYTES,
  paths,
  type CompanyDoc,
  type CompanyDocCategory,
  type DocSend,
} from "@clevis/shared-types";
import { Button, Card, CardTitle, FilePicker, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db, storage } from "../../lib/firebase";
import { call, CallError } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useAccess } from "../../hooks/useAccess";
import { CustomerPicker } from "../../components/CustomerPicker";
import { categoryLabel, expiryState, fmtExpiry } from "../../components/SendDocuments";
import { textMatches } from "../../lib/filter";
import { textareaClass } from "../../components/JobEditCard";
import { DocumentSetsCard } from "../../components/DocumentSetsCard";

// Setup → Company documents: the W-9, certificates of insurance, licenses and
// bonds customers ask for. Send them from any customer, project or job
// ("Send documents"); every send is logged here too. Replacing a file keeps
// the old one, so the log always shows exactly what each customer got.

type DocRow = CompanyDoc & { id: string };
type SendRow = DocSend & { id: string };
const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const fmtWhen = (ms: number) => new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtSize = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const toDateInput = (ms: number | null) => (ms === null ? "" : new Date(ms).toISOString().slice(0, 10));
const fromDateInput = (s: string) => (s ? Date.parse(`${s}T00:00:00Z`) : null);

interface FormState {
  docId: string | null;
  name: string;
  category: CompanyDocCategory;
  folder: string;
  expires: string;
  notes: string;
  forCustomerId: string;
  forCustomerName: string;
  file: File | null;
}
const EMPTY: FormState = { docId: null, name: "", category: "w9", folder: "", expires: "", notes: "", forCustomerId: "", forCustomerName: "", file: null };

export function DocumentsPage() {
  const { tenantId } = useAuth();
  const can = useAccess();
  const mayEdit = can("documents");
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [sends, setSends] = useState<SendRow[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [logSearch, setLogSearch] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(collection(db, paths.tenantCompanyDocs(tenantId)), (snap) =>
        setDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as CompanyDoc) })).sort((a, b) => a.name.localeCompare(b.name))),
      ),
      onSnapshot(query(collection(db, paths.tenantDocSends(tenantId)), orderBy("sentAt", "desc"), limit(200)), (snap) =>
        setSends(snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocSend) }))),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const active = docs.filter((d) => !d.archivedAt);
  // The shared drive's folders (LiftMaster brochures, Insurance…), "" = no folder.
  const folders = [...new Set(docs.map((d) => (d.folder ?? "").trim()))].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  const [folderFilter, setFolderFilter] = useState("all");
  const shownActive = active.filter((d) => folderFilter === "all" || (d.folder ?? "").trim() === folderFilter);
  const archived = docs.filter((d) => d.archivedAt);
  const attention = active.filter((d) => expiryState(d.expiresAt) !== null);
  const shownSends = useMemo(
    () =>
      sends.filter((s) =>
        textMatches(logSearch, s.customerName, s.projectName, s.jobTitle, s.sentByName, s.subject, ...s.to, ...s.cc, ...s.docs.map((d) => d.name)),
      ),
    [sends, logSearch],
  );

  function edit(d: DocRow) {
    setError("");
    setForm({
      docId: d.id,
      name: d.name,
      category: d.category,
      folder: d.folder ?? "",
      expires: toDateInput(d.expiresAt),
      notes: d.notes,
      forCustomerId: d.forCustomerId ?? "",
      forCustomerName: d.forCustomerName,
      file: null,
    });
  }

  async function save() {
    if (!tenantId || !form) return;
    if (!form.name.trim()) return setError("Give it a name customers will recognize, like \"W-9 (2026)\".");
    if (!form.docId && !form.file) return setError("Choose the file.");
    if (form.file && form.file.size > COMPANY_DOC_MAX_BYTES) return setError("Files can be up to 20 MB.");
    setBusy(true);
    setError("");
    try {
      const docId = form.docId ?? doc(collection(db, paths.tenantCompanyDocs(tenantId))).id;
      let storagePath: string | undefined;
      if (form.file) {
        const safe = form.file.name.replace(/[^\w.\- ()]+/g, "_").slice(-100) || "document";
        storagePath = paths.companyDocStorage(tenantId, docId, `${Date.now()}-${safe}`);
        await uploadBytes(storageRef(storage, storagePath), form.file, { contentType: form.file.type || "application/octet-stream" });
      }
      await call("saveCompanyDoc", {
        tenantId,
        docId,
        name: form.name.trim(),
        category: form.category,
        folder: form.folder.trim(),
        notes: form.notes,
        expiresAt: fromDateInput(form.expires),
        forCustomerId: form.forCustomerId || null,
        storagePath,
      });
      setForm(null);
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  async function view(path: string) {
    try {
      window.open(await getDownloadURL(storageRef(storage, path)), "_blank", "noopener");
    } catch {
      setError("That file couldn't be opened.");
    }
  }

  const category = COMPANY_DOC_CATEGORIES.find((c) => c.id === form?.category);
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold">Company documents</h1>
          <p className="text-xs text-steel">
            Your W-9, certificates of insurance, licenses and bonds. Send them from any customer, project or job with "Send
            documents". Every send is logged below.
          </p>
        </div>
        {mayEdit && !form ? (
          <Button className="shrink-0" onClick={() => setForm({ ...EMPTY })}>
            <Upload size={14} className="mr-1 inline" /> Add document
          </Button>
        ) : null}
      </div>

      {attention.length ? (
        <div className="space-y-1 rounded-lg bg-brandorange/10 p-2 text-xs text-ink">
          {attention.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2">
              <span>
                <strong>{d.name}</strong> {expiryState(d.expiresAt) === "expired" ? "expired" : "expires"} {fmtExpiry(d.expiresAt!)}.
                {" "}Upload the renewal with Replace file.
              </span>
              {mayEdit ? (
                <button className="shrink-0 text-brandorange-dark underline" onClick={() => edit(d)}>
                  Replace file
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {form ? (
        <Card className="space-y-3">
          <CardTitle>{form.docId ? "Edit document" : "Add a document"}</CardTitle>
          <div>
            <Label>{form.docId ? "Replace file (optional)" : "File"}</Label>
            <FilePicker
              file={form.file}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,image/*,application/pdf"
              hint="PDF, photo or Office file, or drop it here"
              onChange={(file) => setForm((f) => (f ? { ...f, file, name: f.name || (file?.name.replace(/\.[^.]+$/, "") ?? "") } : f))}
            />
            <p className="mt-0.5 text-[11px] text-steel">PDF or a photo, up to 20 MB. A replaced file is kept, so the log still shows what was sent.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Name customers see</Label>
              <Input value={form.name} maxLength={120} placeholder="W-9 (2026)" onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Kind</Label>
              <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as CompanyDocCategory })}>
                {COMPANY_DOC_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Folder (optional)</Label>
              <Input list="doc-folders" value={form.folder} maxLength={60} placeholder="e.g. LiftMaster brochures, Insurance" onChange={(e) => setForm({ ...form, folder: e.target.value })} />
              <datalist id="doc-folders">
                {folders.filter(Boolean).map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            </div>
            <div>
              <Label>Good through{category?.expires ? "" : " (optional)"}</Label>
              <Input type="date" value={form.expires} onChange={(e) => setForm({ ...form, expires: e.target.value })} />
              <p className="mt-0.5 text-[11px] text-steel">Clevis warns 30 days ahead and asks before sending an expired one.</p>
            </div>
            <div>
              <Label>Made out to one customer (optional)</Label>
              {form.forCustomerId ? (
                <div className="flex items-center gap-2 text-sm text-ink">
                  {form.forCustomerName || "Customer"}
                  <button className="text-xs text-steel underline" onClick={() => setForm({ ...form, forCustomerId: "", forCustomerName: "" })}>
                    any customer
                  </button>
                </div>
              ) : tenantId ? (
                <CustomerPicker
                  tenantId={tenantId}
                  value=""
                  onChange={(c) => setForm({ ...form, forCustomerId: c?.id ?? "", forCustomerName: c?.displayName ?? "" })}
                />
              ) : null}
              <p className="mt-0.5 text-[11px] text-steel">e.g. a certificate naming KB Home as holder: offered only on KB Home.</p>
            </div>
          </div>
          <div>
            <Label>Notes (only your team sees these)</Label>
            <textarea className={textareaClass} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
          <div className="flex gap-2">
            <Button busy={busy} onClick={() => void save()}>
              {form.docId ? "Save" : "Add document"}
            </Button>
            <Button variant="ghost" onClick={() => setForm(null)}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}

      <Card className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
          <CardTitle className="mb-0">Documents ({active.length})</CardTitle>
          {folders.filter(Boolean).length ? (
            <Select className="w-auto py-1 text-xs" aria-label="Folder" value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)}>
              <option value="all">All folders</option>
              {folders.map((f) => (
                <option key={f || "(none)"} value={f}>
                  {f || "No folder"} ({active.filter((d) => (d.folder ?? "").trim() === f).length})
                </option>
              ))}
            </Select>
          ) : null}
        </div>
        {folders
          .filter((f) => shownActive.some((d) => (d.folder ?? "").trim() === f))
          .map((f) => (
            <div key={f || "(none)"}>
              {folders.filter(Boolean).length ? (
                <div className="mt-2 border-b border-steel/10 bg-cream/50 px-4 py-1 text-[11px] font-semibold uppercase tracking-wide text-steel">{f || "No folder"}</div>
              ) : null}
              {shownActive
                .filter((d) => (d.folder ?? "").trim() === f)
                .map((d) => (
                  <DocLine key={d.id} d={d} mayEdit={mayEdit} onView={view} onEdit={() => edit(d)} tenantId={tenantId!} />
                ))}
            </div>
          ))}
        {active.length === 0 ? (
          <p className="px-4 pb-3 text-xs text-steel">
            {mayEdit ? "Add your W-9 and certificate of insurance first: they're what builders ask for." : "None yet. An admin adds them here."}
          </p>
        ) : null}
        {archived.length ? (
          <button className="block w-full px-4 py-1.5 text-left text-xs text-steel hover:text-ink" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Hide" : "Show"} archived ({archived.length})
          </button>
        ) : null}
        {showArchived ? archived.map((d) => <DocLine key={d.id} d={d} mayEdit={mayEdit} onView={view} onEdit={() => edit(d)} tenantId={tenantId!} />) : null}
      </Card>

      {tenantId ? <DocumentSetsCard tenantId={tenantId} docs={active.map((d) => ({ id: d.id, name: d.name }))} mayEdit={mayEdit} /> : null}

      <Card className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
          <CardTitle className="mb-0">Send log</CardTitle>
          <Input className="max-w-xs py-1 text-xs" placeholder="Find a customer, email or document…" value={logSearch} onChange={(e) => setLogSearch(e.target.value)} />
        </div>
        <div className="mt-2">
          {shownSends.map((s) => (
            <div key={s.id} className="border-b border-steel/10 px-4 py-1.5 text-xs last:border-0">
              <div className="flex justify-between gap-2">
                <span className={cn("min-w-0 truncate font-semibold", s.status === "sent" ? "text-ink" : "text-danger")}>
                  {s.status === "failed" ? "Not sent: " : ""}
                  {s.docs.map((d) => `${d.name}${d.version > 1 ? ` (v${d.version})` : ""}`).join(", ")}
                </span>
                <span className="flex shrink-0 items-center gap-1.5 text-steel">
                  {s.status === "sent" ? <DeliveryChip delivery={s.delivery} /> : null}
                  {fmtWhen(s.sentAt)} · {s.sentByName}
                </span>
              </div>
              <div className="text-steel">
                {s.customerId ? (
                  <Link href={`/customers/${s.customerId}`} className="text-ink hover:underline">
                    {s.customerName || "Customer"}
                  </Link>
                ) : null}
                {s.projectId ? (
                  <>
                    {" › "}
                    <Link href={`/projects/${s.projectId}`} className="hover:underline">
                      {s.projectName || "project"}
                    </Link>
                  </>
                ) : null}
                {s.jobId ? (
                  <>
                    {" › "}
                    <Link href={`/jobs/${s.jobId}`} className="hover:underline">
                      {s.jobTitle || "job"}
                    </Link>
                  </>
                ) : null}
                {" · "}to {s.to.join(", ")}
                {s.cc.length ? ` · cc ${s.cc.join(", ")}` : ""}
                {s.expiredIncluded ? " · sent past expiration" : ""}
                {s.status === "failed" && s.error ? ` · ${s.error}` : ""}
                {s.delivery?.status === "bounced" ? <span className="text-danger"> · bounced{s.delivery.detail ? `: ${s.delivery.detail}` : ""}</span> : null}
              </div>
              <div className="truncate text-steel">Subject: {s.subject}</div>
            </div>
          ))}
          {shownSends.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">{sends.length ? "Nothing matches." : "Nothing sent yet."}</p> : null}
        </div>
      </Card>
    </div>
  );
}

function DocLine({ d, mayEdit, onView, onEdit, tenantId }: { d: DocRow; mayEdit: boolean; onView: (path: string) => void; onEdit: () => void; tenantId: string }) {
  const ex = expiryState(d.expiresAt);
  const [busy, setBusy] = useState(false);
  return (
    <div className={cn("flex items-center gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0", d.archivedAt && "opacity-60")}>
      <FileText size={14} className="shrink-0 text-steel" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-ink">{d.name}</div>
        <div className="truncate text-xs text-steel">
          {categoryLabel(d.category)}
          {d.expiresAt ? (
            <span className={cn(ex === "expired" ? "text-danger" : ex === "soon" ? "text-brandorange-dark" : undefined)}>
              {" · "}
              {ex === "expired" ? "expired" : "good through"} {fmtExpiry(d.expiresAt)}
            </span>
          ) : null}
          {d.forCustomerId ? ` · for ${d.forCustomerName || "one customer"}` : ""}
          {" · "}
          {d.file.fileName} ({fmtSize(d.file.sizeBytes)}
          {d.file.version > 1 ? `, v${d.file.version}` : ""})
          {d.sendCount ? ` · sent ${d.sendCount}×, last ${fmtDay(d.lastSentAt!)}` : " · not sent yet"}
          {d.updatedByName ? ` · updated by ${d.updatedByName}` : ""}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 text-xs">
        <button className="text-steel underline hover:text-ink" onClick={() => onView(d.file.storagePath)}>
          View
        </button>
        {mayEdit ? (
          <>
            {!d.archivedAt ? (
              <button className="text-steel underline hover:text-ink" onClick={onEdit}>
                Edit
              </button>
            ) : null}
            <button
              className="text-steel underline hover:text-ink disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void call("archiveCompanyDoc", { tenantId, docId: d.id, archived: !d.archivedAt }).finally(() => setBusy(false));
              }}
            >
              {d.archivedAt ? "Restore" : "Archive"}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
